import type { PerformanceEntryBuffer } from './BufferWithMaxLength.tsx';
import type { LayoutShiftPerformanceEntry } from './LayoutShiftPerformanceEntry.tsx';
/**
 * Provides a metric reflecting the visual stability of a webpage by calculating the largest burst of layout shifts within a specified time frame. Useful for assessing user experience quality.
 */
export const getCLS = (start: number, stop: number, buffer: PerformanceEntryBuffer): number => {
	const layoutShifts = buffer
		.getAll()
		.filter((entry) => entry.startTime >= start && entry.startTime <= stop);
	const sessionWindows = [];
	let currentWindow = null;

	// Group layout shifts into session windows
	for (const shift of layoutShifts) {
		const { startTime } = shift;
		const endTime = shift.startTime + shift.duration;
		// oxlint-disable-next-line typescript/consistent-type-assertions
		const score = (shift as LayoutShiftPerformanceEntry).value;
		if (
			currentWindow === null ||
			startTime - currentWindow.endTime > 1000 ||
			endTime - currentWindow.startTime > 5000
		) {
			// Start a new session window
			currentWindow = {
				startTime,
				endTime,
				score,
			};
			sessionWindows.push(currentWindow);
		} else {
			// Add layout shift to current session window
			currentWindow.endTime = endTime;
			currentWindow.score += score;
		}
	}

	// Find session window with highest cumulative score
	const maxScore = sessionWindows.reduce(
		(max, sessionWindow) => (sessionWindow.score > max ? sessionWindow.score : max),
		0,
	);

	// Return score of largest burst as CLS metric
	return Math.round(maxScore * 10000) / 10000;
};
